import React, { useState } from 'react';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useIntl } from 'react-intl';
import { Button, Tabs, Table, DatePicker, Dropdown, Input, Modal } from 'ui';
import { DownOutlined } from '@ant-design/icons';
import Filter from 'admin/components/Filter';
import { StyledButtonList } from 'styles/buttonList';
import dayjs from 'dayjs';
import EnumSelect from '../../../Selects/EnumSelect';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import FileDownload from 'js-file-download';
import { MenuProps } from 'antd/es/menu';
import { Pagination } from '../../../../types/Pagination';
import { print } from '../../../../utils/print';
import copyToClipboard from '../../../../utils/copyToClipboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faMinus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { OrdersViewOrder } from '../../Orders/OrdersViewOrder/OrdersViewOrder';
import { adminToken } from '../../../../config/token';
import { set } from 'js-cookie';

interface Order {
  key: React.Key;
  act_nr: string | null;
  billing_status: number | null;
  brand: string;
  created_at: string;
  delivery_comments: string | null;
  farm_delivery_id: number | null;
  farm_id: number;
  farm_payout_at: string | null;
  horizon_href: string | null;
  id: number;
  invoice_nr: string;
  name: string;
  number: string | null;
  order_id: string;
  order_user_id: number | null;
  reg_nr: string;
  status: string;
  status_comments: string | null;
  total_sum: String;
  transaction: string | null;
  updated_at: string;
  user_id: number | null;
}

const ACTION_KEYS = {
  BUYER_ORDER: 'buyer_order',
  FARM_ORDER: 'farm_order',
  PAID: '1',
  NOT_PAID: '0',
  CANCELED: '2',
};

const iconSize = adminToken.adminIconSize;

const iconColors = {
  green: adminToken.iconGreen,
  red: adminToken.iconRed,
  blue: adminToken.iconBlue,
};

const statusKeys = [ACTION_KEYS.PAID, ACTION_KEYS.NOT_PAID];

const TransactionListPage = () => {
  const [advancedSearch, setAdvancedSearch] = useState({});
  const [reloadPage, setReloadPage] = useState<number>(0);
  const [idsToCancel, setIdsToCancel] = useState<React.Key[]>([]);
  const [ordertData, setOrderData] = useState<Order>();
  const [paginationStatus, setPaginationStatus] = useState<Pagination>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [viewOrderData, setViewOrderData] = useState();
  const [isOpenViewOrderDrawer, setIsOpenViewOrderDrawer] =
    useState<boolean>(false);
  const [isOnStatusChange, setIsOnStatusChange] = useState<boolean>(false);
  const intl = useIntl();

  const { appendData: fetchOrder } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/:transactionId',
      disableOnMount: true,
    },
    onSuccess: (response) => handleSuccessOrderFetch(response),
  });

  const { appendData } = useQueryApiClient({
    request: {
      url: `api/v2/fidavista`,
      multipart: true,
      method: 'POST',
    },
    onSuccess: (response) => {
      FileDownload(response, `Fidavista_${dayjs().format('YYYY-MM-DD')}.xml`);
    },
  });

  const { appendData: exportData } = useQueryApiClient({
    request: {
      url: `api/v2/transactions/export`,
      data: {
        filter: advancedSearch,
      },
      multipart: true,
      method: 'POST',
    },
    onSuccess: (response, format) => {
      FileDownload(
        response,
        `${format}_${dayjs().format('YYYY-MM-DD')}.${format}`
      );
    },
  });

  const { appendData: changeStatus } = useQueryApiClient({
    request: {
      url: `api/v2/transactions/change-billing-status`,
      method: 'POST',
    },
    onSuccess: () => {
      setIsOnStatusChange(false);
      setReloadPage(reloadPage + 1);
    },
    onError: () => {
      setIsOnStatusChange(false);
    },
  });

  const handleSuccessOrderFetch = (response: any) => {
    setViewOrderData(response);
    setIsOpenViewOrderDrawer(true);
  };

  const handleCloseViewOrderDrawer = () => {
    setViewOrderData(undefined);
    setIsOpenViewOrderDrawer(false);
  };

  const headers = [
    intl.formatMessage({ id: 'transactions.date' }),
    intl.formatMessage({ id: 'transactions.farm_name' }),
    intl.formatMessage({ id: 'transactions.settlement_deed' }),
    intl.formatMessage({ id: 'transactions.order_nr' }),
    intl.formatMessage({ id: 'transactions.invoice_nr' }),
    intl.formatMessage({ id: 'transactions.status' }),
    intl.formatMessage({ id: 'transactions.status_desc' }),
    intl.formatMessage({ id: 'transactions.bill_status' }),
    intl.formatMessage({ id: 'transactions.price' }),
  ].join('\t');

  const getBillingStatus = (value: number) => {
    switch (value) {
      case 0:
        return intl.formatMessage({ id: 'enum.NOT_PAID' });
      case 1:
        return intl.formatMessage({ id: 'enum.PAID' });
      case 2:
        return intl.formatMessage({ id: 'enum.CANCELED' });
    }
    return '';
  };

  const formatRow = (row: Order) => {
    return [
      row.created_at ? dayjs(row.created_at).format('DD.MM.YYYY') : '',
      row.name || '',
      row.act_nr || '',
      row.number || '',
      row.invoice_nr || '',
      row.status || '',
      row.status_comments || '',
      getBillingStatus(row.billing_status ?? 0),
      row.total_sum || '',
    ].join('\t');
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'transactions.date' }),
      dataIndex: 'created_at',
      render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: intl.formatMessage({ id: 'transactions.farm_name' }),
      dataIndex: 'name',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'transactions.settlement_deed' }),
      dataIndex: 'act_nr', //todo
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'transactions.order_nr' }),
      dataIndex: 'number',
      render: (value: string) => value || 'Nav numura',
    },
    {
      title: intl.formatMessage({ id: 'transactions.invoice_nr' }),
      dataIndex: 'invoice_nr',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'transactions.status' }),
      dataIndex: 'status',
      render: (value: string) =>
        intl.formatMessage({ id: `enum.${value.toUpperCase()}` }),
    },
    {
      title: intl.formatMessage({ id: 'transactions.status_desc' }),
      dataIndex: 'status_comments',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'transactions.bill_status' }),
      dataIndex: 'billing_status',
      render: (value: number) => getBillingStatus(value),
    },
    {
      title: intl.formatMessage({ id: 'transactions.price' }),
      dataIndex: 'total_sum',
      render: (value: string) => Number(value).toFixed(2),
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      dataIndex: 'id',
      render: (value: Order, record: Order) => {
        return (
          <Dropdown
            menu={{
              items: [
                {
                  label: intl.formatMessage({ id: 'general.buyer_order' }),
                  key: 'buyer_order',
                },
                {
                  label: intl.formatMessage({ id: 'general.farm_order' }),
                  key: 'farm_order',
                },
                ...(record.status !== 'canceled'
                  ? [
                      {
                        label: intl.formatMessage({ id: 'general.paid' }),
                        key: '1',
                      },
                      {
                        label: intl.formatMessage({ id: 'general.not_paid' }),
                        key: '0',
                      },
                      {
                        label: intl.formatMessage({ id: 'general.canceled' }),
                        key: '2',
                      },
                    ]
                  : []),
              ],
              onClick: (value) => {
                if (value.key === ACTION_KEYS.FARM_ORDER) {
                  fetchOrder({}, { transactionId: record.id });
                  return;
                }

                if (value.key === ACTION_KEYS.BUYER_ORDER) {
                  window.open(
                    `/user/order?order_id=${record.order_id}&date=${dayjs(
                      record.created_at
                    ).format('DD.MM.YYYY')}`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }

                if (value.key === ACTION_KEYS.CANCELED) {
                  setIdsToCancel([record.id]);
                  return;
                }

                if (statusKeys.includes(value.key)) {
                  setIsOnStatusChange(true);
                  changeStatus({
                    status: value.key,
                    id: record.id,
                  });
                }
              },
            }}
          >
            <Button
              className="action"
              label={intl.formatMessage({ id: 'general.actions' })}
              icon={<DownOutlined />}
              iconPosition="end"
              size="middle"
            />
          </Dropdown>
        );
      },
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: any = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const items = [
    {
      key: 'active_transactions',
      label: intl.formatMessage({ id: 'transactions.active_transactions' }),
      children: (
        <>
          <Filter
            onFinish={setAdvancedSearch}
            totalRecord={paginationStatus?.total ?? 0}
          >
            <DatePicker
              label={intl.formatMessage({ id: 'general.date_from' })}
              name="date_from"
            />
            <DatePicker
              label={intl.formatMessage({ id: 'general.date_to' })}
              name="date_to"
            />
            <EnumSelect
              label={intl.formatMessage({ id: 'transactions.delivered' })}
              name="delivered"
              code="PurchaseStatusType"
            />
            <EnumSelect
              label={intl.formatMessage({ id: 'transactions.bill_status' })}
              name="billing_status"
              code="BillStatusType"
            />
            <Input
              label={intl.formatMessage({ id: 'general.search' })}
              name="search"
            />
            <Button
              type="primary"
              label={intl.formatMessage({ id: 'general.select' })}
              htmlType="submit"
            />
          </Filter>
          <Dropdown
            menu={{
              items: [
                {
                  icon: (
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ fontSize: iconSize, color: iconColors.green }}
                    />
                  ),
                  label: intl.formatMessage({ id: 'general.paid' }),
                  key: 1,
                },
                {
                  icon: (
                    <FontAwesomeIcon
                      icon={faXmark}
                      style={{ fontSize: iconSize, color: iconColors.red }}
                    />
                  ),
                  label: intl.formatMessage({ id: 'general.canceled' }),
                  key: 2,
                },
                {
                  icon: (
                    <FontAwesomeIcon
                      icon={faMinus}
                      style={{ fontSize: iconSize, color: iconColors.blue }}
                    />
                  ),
                  label: intl.formatMessage({ id: 'general.not_paid' }),
                  key: 0,
                },
              ],
              onClick: (value) => {
                if (value.key === '2') {
                  setIdsToCancel(selectedRowKeys);
                  return;
                }

                changeStatus({ status: value.key, row: selectedRowKeys });
              },
            }}
          >
            <Button
              label={intl.formatMessage({ id: 'general.actions' })}
              type="primary"
              disabled={selectedRowKeys.length === 0}
            />
          </Dropdown>
          <Table
            url="/api/v2/transactions"
            columns={columns}
            filter={advancedSearch}
            reload={reloadPage}
            rowKey={'id'}
            saveData={(el: Order) => {
              setOrderData(el);
            }}
            paginnationStatus={(el: object) => {
              setPaginationStatus(el);
            }}
            rowSelectionFunction={rowSelection}
            enableSelectedRow={true}
            loading={isOnStatusChange}
          />
        </>
      ),
    },
    {
      key: 'old_transactions',
      label: intl.formatMessage({ id: 'transactions.old_transactions' }),
      children: `Content of tab`,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    switch (e.key) {
      case 'copy':
        copyToClipboard({
          data: ordertData,
          headers: headers,
          formatRow: formatRow,
          intl: intl,
        });
        break;
      case 'xlsx':
      case 'csv':
        exportData({ format: e.key, ...advancedSearch }, [], e.key);
        break;
      case 'print':
        print(intl.formatMessage({ id: 'general.printing_header' }), 10);
        break;
    }
  };

  const dropdownItems: MenuProps['items'] = [
    {
      label: intl.formatMessage({ id: 'general.print' }),
      key: 'print',
    },
    {
      label: intl.formatMessage({ id: 'general.copy' }),
      key: 'copy',
    },
    {
      label: intl.formatMessage({ id: 'general.excel' }),
      key: 'xlsx',
    },
    {
      label: intl.formatMessage({ id: 'general.csv' }),
      key: 'csv',
    },
  ];

  const menuProps = {
    items: dropdownItems,
    onClick: handleMenuClick,
  };

  return (
    <DefaultLayout.PageLayout
      breadcrumbs={[
        {
          title: intl.formatMessage({ id: 'navigation.beginning' }),
        },
        {
          title: intl.formatMessage({ id: 'navigation.transactions' }),
        },
      ]}
    >
      <DefaultLayout.PageContent>
        <Modal
          open={idsToCancel.length > 0}
          onCancel={() => setIdsToCancel([])}
          destroyOnClose
          footer={[
            <Button
              key="yes"
              type="primary"
              onClick={() => {
                setIsOnStatusChange(true);

                changeStatus({
                  status: ACTION_KEYS.CANCELED,
                  row: idsToCancel,
                });

                setIdsToCancel([]);
              }}
              label={intl.formatMessage({ id: 'general.yes' })}
            />,
            <Button
              key="no"
              type="primary"
              onClick={() => setIdsToCancel([])}
              label={intl.formatMessage({ id: 'general.no' })}
            />,
          ]}
        >
          <h3>{intl.formatMessage({ id: 'general.cancel_purchase_info' })}</h3>
          <p>{intl.formatMessage({ id: 'general.transactions_warning' })}</p>
        </Modal>
        <Tabs
          tabBarExtraContent={
            <StyledButtonList>
              <Button
                type="primary"
                label={intl.formatMessage({ id: 'transactions.fida' })}
                onClick={() => appendData()}
              />
              <Dropdown menu={menuProps}>
                <Button
                  label={intl.formatMessage({ id: 'general.export' })}
                  type="primary"
                />
              </Dropdown>
            </StyledButtonList>
          }
          items={items}
        />
        <OrdersViewOrder
          open={isOpenViewOrderDrawer}
          onClose={handleCloseViewOrderDrawer}
          transaction={viewOrderData}
          reloadPage={reloadPage}
          setReload={setReloadPage}
        />
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default TransactionListPage;
